import React, { useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { Button, Col, Row, Input, message, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { useHistory, useParams } from 'react-router-dom';

import { PageTitle } from '../../components/PageTitle';
import { CanvasObject } from './CanvasBuilder/types';
import { CanvasBuilderContainer } from './CanvasBuilder/styles';
import { useCanvasInteractions } from './CanvasBuilder/hooks/useCanvasInteractions';
import { PropertiesPanel } from './CanvasBuilder/components/PropertiesPanel';
import { CanvasObjectComponent } from './CanvasBuilder/components/CanvasObject';
import { PreviewModal } from './CanvasBuilder/components/PreviewModal';
import { ImageUploadModal } from './CanvasBuilder/components/ImageUploadModal';
import { Toolbar } from './CanvasBuilder/components/Toolbar';
import { ConnectorArrow } from './CanvasBuilder/components/ConnectorArrow';
import { createObject, captureCanvas, getCanvasCoordinates } from './CanvasBuilder/utils';
import { SavePreviewModal } from './CanvasBuilder/components/SavePreviewModal';
import { TOOLS } from './CanvasBuilder/constants';
import { useRootStore } from '../../context/storeContext';

interface RouteParams {
  templateId: string;
}

export const TemplateCreate = observer(() => {
  const history = useHistory();
  const canvasRef = useRef<HTMLDivElement>(null);
  const { templatesStore, transportLayer } = useRootStore();
  const { templateId } = useParams<RouteParams>();

  // State
  const [objects, setObjects] = useState<CanvasObject[]>([]);
  const [selectedTool, setSelectedTool] = useState<string>('select');
  const [selectedObjects, setSelectedObjects] = useState<string[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [pendingImagePosition, setPendingImagePosition] = useState<{ x: number; y: number } | null>(null);
  const [draggedTool, setDraggedTool] = useState<string | null>(null);
  const [dragPreview, setDragPreview] = useState<{ x: number; y: number; tool: ReactNode } | null>(null);
  const [endpointDrag, setEndpointDrag] = useState<{
    objectId: string;
    endpoint: 'start' | 'end' | 'breakpoint';
    startMouseX: number;
    startMouseY: number;
    startPointX: number;
    startPointY: number;
  } | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
  const [showSavePreview, setShowSavePreview] = useState(false);
  const [, setIsSaving] = useState(false);

  // Custom hook for canvas interactions
  const {
    selectionBox,
    handleCanvasClick,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleObjectMouseDown,
    handleObjectMouseMove,
    handleResizeStart,
    duplicateSelectedObjects,
    deleteSelectedObjects,
  } = useCanvasInteractions(objects, setObjects, selectedObjects, setSelectedObjects, canvasRef);

  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const [canvasScale, setCanvasScale] = useState(1);

  useEffect(() => {
    if (!canvasWrapperRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      const PADDING = 20;
      const scaleX = (width - PADDING) / 1920;
      const scaleY = (height - PADDING) / 1080;
      const scale = Math.min(scaleX, scaleY, 1.2);
      setCanvasScale(Math.max(0.1, scale));
    });

    observer.observe(canvasWrapperRef.current);
    return () => observer.disconnect();
  }, []);

  // Load the guided-doing template on mount
  useEffect(() => {
    const loadTemplate = async () => {
      setIsLoadingTemplate(true);
      try {
        await templatesStore.fetchGuidedDoingTemplates();
        const template = templatesStore.guidedDoingTemplates.find(t => t.id === templateId);
        if (template) {
          setTemplateName(template.title);
          if (template.objects) {
            setObjects(template.objects as unknown as CanvasObject[]);
          }
        } else {
          message.error('Template not found');
          history.push('/organization-templates');
        }
      } finally {
        setIsLoadingTemplate(false);
      }
    };
    loadTemplate();
  }, [templateId, templatesStore, history]);

  // Inject fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/fonts/fonts.css';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Handlers
  const handleToolSelect = (tool: string) => {
    setSelectedTool(tool);
  };

  const handleToolDragStart = (e: React.DragEvent, toolKey: string) => {
    if (toolKey === 'select') return;
    setDraggedTool(toolKey);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', toolKey);

    const dragImage = document.createElement('div');
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.width = '1px';
    dragImage.style.height = '1px';
    dragImage.style.opacity = '0';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);

    const tool = TOOLS.find(t => t.key === toolKey);
    if (tool) {
      setDragPreview({ x: e.clientX, y: e.clientY, tool: tool.icon });
    }
  };

  const handleToolDragEnd = () => {
    setDraggedTool(null);
    setDragPreview(null);
  };

  const handleToolDrag = (e: React.DragEvent) => {
    if (dragPreview && e.clientX !== 0 && e.clientY !== 0) {
      setDragPreview(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
    }
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    if (draggedTool) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedTool || !canvasRef.current) return;
    const { x, y } = getCanvasCoordinates(e, canvasRef);
    if (draggedTool === 'image') {
      setPendingImagePosition({ x: x - 75, y: y - 75 });
      setShowImageUpload(true);
    } else {
      const newObject = createObject(draggedTool, x - 50, y - 25);
      setObjects(prev => [...prev, newObject]);
      setSelectedObjects([newObject.id]);
    }
    setDraggedTool(null);
  };

  const handleImageToolClick = (x: number, y: number) => {
    setPendingImagePosition({ x, y });
    setShowImageUpload(true);
  };

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result && pendingImagePosition) {
        const imageUrl = e.target.result as string;
        const newObject = createObject('image', pendingImagePosition.x, pendingImagePosition.y);
        if (newObject.type === 'image') {
          newObject.imageUrl = imageUrl;
          newObject.imagePath = file.name;
        }
        setObjects(prev => [...prev, newObject]);
        setSelectedObjects([newObject.id]);
        setSelectedTool('select');
        setShowImageUpload(false);
        setPendingImagePosition(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageUploadCancel = () => {
    setShowImageUpload(false);
    setPendingImagePosition(null);
    setSelectedTool('select');
  };

  const handleEndpointDragStart = (e: React.MouseEvent, objectId: string, endpoint: 'start' | 'end' | 'breakpoint') => {
    e.stopPropagation();
    const obj = objects.find((o) => o.id === objectId);
    if (!obj || obj.type !== 'arrow') return;
    setEndpointDrag({
      objectId,
      endpoint,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startPointX: endpoint === 'start' ? (obj.position?.x ?? 0) :
        endpoint === 'end' ? (obj.endPosition?.x ?? obj.size?.width) :
          (obj.breakpointX ?? ((obj.position?.x ?? 0) + (obj.endPosition?.x ?? obj.size?.width)) / 2),
      startPointY: endpoint === 'start' ? (obj.position?.y ?? 0) :
        endpoint === 'end' ? (obj.endPosition?.y ?? obj.size?.height) :
          (obj.breakpointY ?? ((obj.position?.y ?? 0) + (obj.endPosition?.y ?? obj.size?.height)) / 2),
    });
  };

  const handleEndpointDragMove = (e: React.MouseEvent) => {
    if (!endpointDrag) return;
    const deltaX = e.clientX - endpointDrag.startMouseX;
    const deltaY = e.clientY - endpointDrag.startMouseY;
    setObjects((prev) =>
      prev.map((obj) => {
        if (obj.id !== endpointDrag.objectId || obj.type !== 'arrow') return obj;
        if (endpointDrag.endpoint === 'start') {
          return { ...obj, position: { x: endpointDrag.startPointX + deltaX, y: endpointDrag.startPointY + deltaY } };
        } else if (endpointDrag.endpoint === 'end') {
          return { ...obj, endPosition: { x: endpointDrag.startPointX + deltaX, y: endpointDrag.startPointY + deltaY } };
        } else if (endpointDrag.endpoint === 'breakpoint') {
          return { ...obj, breakpointX: endpointDrag.startPointX + deltaX, breakpointY: endpointDrag.startPointY + deltaY };
        }
        return obj;
      })
    );
  };

  const handleEndpointDragEnd = () => {
    setEndpointDrag(null);
  };

  const updateSelectedObjects = (updates: Partial<CanvasObject>) => {
    setObjects(prev => prev.map(obj => {
      if (!selectedObjects.includes(obj.id)) return obj;
      return { ...obj, ...updates } as CanvasObject;
    }));
  };

  const handleObjectSelect = (objectId: string) => {
    setSelectedObjects([objectId]);
  };

  const handleObjectsReorder = (newObjects: CanvasObject[]) => {
    setObjects(newObjects);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      message.error('Please enter a template name');
      return;
    }
    if (objects.length === 0) {
      message.error('Please add some objects to the canvas');
      return;
    }

    const CAPTURE_KEY = 'capture-preview';
    message.loading({ content: 'Capturing canvas preview...', key: CAPTURE_KEY, duration: 0 });
    try {
      setSelectedObjects([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      if (canvasRef.current) {
        const blob = await captureCanvas(canvasRef.current);
        if (blob && blob.size > 0) {
          setCapturedBlob(blob);
          const url = URL.createObjectURL(blob);
          setCapturedImageUrl(url);
          setShowSavePreview(true);
        } else {
          message.error({ content: 'Failed to capture canvas preview', key: CAPTURE_KEY });
        }
      }
    } catch (error) {
      message.error({ content: 'An error occurred while capturing the template', key: CAPTURE_KEY });
      console.error('Capture template error:', error);
    } finally {
      message.destroy(CAPTURE_KEY);
    }
  };

  const handleFinalSave = async () => {
    if (!capturedBlob) return;
    setIsSaving(true);
    const SAVE_KEY = 'save-template';
    message.loading({ content: 'Creating template...', key: SAVE_KEY, duration: 0 });
    try {
      const cleanedObjects = objects.map(obj => {
        const newObj = { ...obj } as any;
        delete newObj.rotation;
        Object.keys(newObj).forEach(key => {
          if (newObj[key] === null || newObj[key] === undefined) {
            delete newObj[key];
          }
        });
        return newObj as CanvasObject;
      });

      // Always create a new template (never update the base guided-doing template)
      const result = await templatesStore.createCanvasTemplate(templateName, cleanedObjects);

      if (result.succeeded && result.data) {
        const newTemplateId = result.data;
        const file = new File([capturedBlob], 'preview.png', { type: 'image/png' });
        const uploadResult = await transportLayer.uploadTemplateFile(newTemplateId, 'preview', file);

        let finalResult: { succeeded: boolean; message?: string } = result;
        if (uploadResult.succeeded && uploadResult.data) {
          const finalUrl = uploadResult.data.cdnUrl || uploadResult.data.fileUrl || '';
          finalResult = await templatesStore.updateCanvasTemplate(newTemplateId, templateName, cleanedObjects, finalUrl);
        }

        if (finalResult.succeeded) {
          message.success('Template created successfully!');
          setShowSavePreview(false);
          history.push('/organization-templates');
        } else {
          message.error(finalResult.message || 'Failed to finalize template');
        }
      } else {
        message.error(result.message || 'Failed to create template');
      }
    } catch (error) {
      message.error('An error occurred while saving the template');
      console.error('Final save error:', error);
    } finally {
      message.destroy(SAVE_KEY);
      setIsSaving(false);
    }
  };

  const onBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const selectedObject = selectedObjects.length === 1 ? objects.find(obj => obj.id === selectedObjects[0]) || null : null;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' && selectedObjects.length > 0) {
        deleteSelectedObjects();
      }
      if (e.key === 'd' && (e.ctrlKey || e.metaKey) && selectedObjects.length > 0) {
        e.preventDefault();
        duplicateSelectedObjects();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedObjects, deleteSelectedObjects, duplicateSelectedObjects]);

  return (
    <>
      <CanvasBuilderContainer>
        <Col span={22}>
          <div className="header-section">
            <Row justify="space-between" align="middle">
              <PageTitle title="Create Template" />
              <div className="header-buttons">
                <Input
                  className="template-name-input"
                  placeholder="Enter template name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  style={{ width: 250 }}
                />
                <Button onClick={() => setShowPreview(true)} disabled={objects.length === 0}>
                  Preview
                </Button>
                <Button onClick={onBack}>
                  Cancel
                </Button>
                <Button type="primary" onClick={handleSaveTemplate}>
                  Save Template
                </Button>
              </div>
            </Row>
          </div>

          <div className="canvas-container" style={{ position: 'relative', height: 'calc(100vh - 120px)', width: '100%' }}>
            <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
              <Toolbar
                TOOLS={TOOLS}
                selectedTool={selectedTool}
                objects={objects}
                selectedObjects={selectedObjects}
                draggedTool={draggedTool}
                onToolSelect={handleToolSelect}
                onToolDragStart={handleToolDragStart}
                onToolDrag={handleToolDrag}
                onToolDragEnd={handleToolDragEnd}
                onDuplicate={duplicateSelectedObjects}
                onDelete={deleteSelectedObjects}
              />
            </div>

            <div className="canvas-area">
              <div className="canvas-wrapper" ref={canvasWrapperRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                <div
                  ref={canvasRef}
                  className="canvas"
                  onClick={(e) => handleCanvasClick(e, selectedTool, handleImageToolClick, setSelectedTool)}
                  onMouseDown={(e) => handleMouseDown(e, selectedTool)}
                  onMouseMove={(e) => {
                    handleMouseMove(e);
                    handleObjectMouseMove(e);
                    if (endpointDrag) handleEndpointDragMove(e);
                  }}
                  onMouseUp={() => {
                    handleMouseUp();
                    handleEndpointDragEnd();
                  }}
                  onMouseLeave={handleEndpointDragEnd}
                  onDragOver={handleCanvasDragOver}
                  onDrop={handleCanvasDrop}
                  style={{
                    border: draggedTool ? '2px dashed #1890ff' : undefined,
                    backgroundColor: draggedTool ? 'rgba(24, 144, 255, 0.05)' : undefined,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(-50%, -50%) scale(${canvasScale})`,
                    transformOrigin: 'center center'
                  }}
                >
                  {isLoadingTemplate ? (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                      <Spin size="large" tip="Loading template objects..." />
                    </div>
                  ) : (
                    <>
                      {objects.map(obj =>
                        obj.type === 'arrow' ? (
                          <ConnectorArrow
                            key={obj.id}
                            obj={obj}
                            isSelected={selectedObjects.includes(obj.id)}
                            onEndpointDrag={handleEndpointDragStart}
                            onMouseDown={handleObjectMouseDown}
                          />
                        ) : (
                          <CanvasObjectComponent
                            key={obj.id}
                            obj={obj}
                            isSelected={selectedObjects.includes(obj.id)}
                            isMultiSelected={selectedObjects.length > 1 && selectedObjects.includes(obj.id)}
                            onMouseDown={handleObjectMouseDown}
                            onMouseMove={() => { }}
                            onResizeStart={handleResizeStart}
                          />
                        )
                      )}
                    </>
                  )}

                  {selectionBox && (
                    <div
                      className="selection-box"
                      style={{
                        left: Math.min(selectionBox.startX, selectionBox.endX),
                        top: Math.min(selectionBox.startY, selectionBox.endY),
                        width: Math.abs(selectionBox.endX - selectionBox.startX),
                        height: Math.abs(selectionBox.endY - selectionBox.startY),
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <PropertiesPanel
              objects={objects}
              selectedObjects={selectedObjects}
              selectedObject={selectedObject}
              onUpdateObjects={updateSelectedObjects}
              onReorderObjects={handleObjectsReorder}
              onSelectObject={handleObjectSelect}
            />
          </div>
        </Col>

        <PreviewModal
          visible={showPreview}
          templateName={templateName}
          objects={objects}
          onClose={() => setShowPreview(false)}
          onSave={handleSaveTemplate}
        />

        <ImageUploadModal
          visible={showImageUpload}
          onUpload={handleImageUpload}
          onCancel={handleImageUploadCancel}
        />

        {dragPreview && (
          <div
            style={{
              position: 'fixed',
              left: dragPreview.x - 20,
              top: dragPreview.y - 20,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#1890ff',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              pointerEvents: 'none',
              zIndex: 9999,
              transform: 'scale(1.1)',
              transition: 'none'
            }}
          >
            {dragPreview.tool}
          </div>
        )}

        <SavePreviewModal
          visible={showSavePreview}
          imageUrl={capturedImageUrl}
          previewUrl={null}
          onCancel={() => {
            setShowSavePreview(false);
            if (capturedImageUrl) URL.revokeObjectURL(capturedImageUrl);
            setCapturedImageUrl(null);
            setCapturedBlob(null);
          }}
          onUpload={handleFinalSave}
          isUpdating={false}
          isBaseTemplate={false}
        />
      </CanvasBuilderContainer>
    </>
  );
});
